<template>
    <div>
        <h2>Advertiser Phone Number Provisioning</h2>

        <p v-show="!advertiserId">Select an advertiser from the list above to manage the provisioned phone numbers.</p>

        <div v-show="advertiserId">
            <a href="javascript:void(0);" type="button" class="btn --green-light --white-font" @click="openProvisionNumberModal()" style="margin: 10px 0;">Provision a Number</a>
            <br-table :columns="leaseTable.columns"
                      :options="leaseTable.options"
                      :client-side="true"
                      order-by="ProvisionNumberID"
                      sort-by="desc"
                      :url="leaseEndpoint"
                      ref="table">
                <template slot="actions" slot-scope="{row}">
                    <a href="#" @click="editProvisionNumber(row.id)">edit</a>
                    <a href="#" @click="deleteProvisionNumber(row.id)">delete</a>
                </template>
                <template slot="advertiserPhoneNumber" slot-scope="{row}">
                    {{ row.advertiserPhoneNumber | phone }}
                </template>
                <template slot="leasedPhoneNumber" slot-scope="{row}">
                    {{ row.leasedPhoneNumber | phone }}
                </template>
                <template slot="shouldShowCallerId" slot-scope="{row}">
                    {{ row.shouldShowCallerId ? 'Yes' : 'No' }}
                </template>
                <template slot="ivrProfile" slot-scope="{row}">
                    {{ getIvrProfileById(row.ivrProfile) }}
                </template>
                <template slot="vertical" slot-scope="{row}">
                    {{ getVerticalById(row.vertical) }}
                </template>
                <template slot="no-data-text">
                    There is no provisioned numbers for the selected advertiser
                </template>
            </br-table>

            <modal :show.sync="isProvisionNumberOpen" title="Provision a Number" @ok="validateProvisionNumber" @cancel="closeProvisionNumberModal" effect="zoom" width="300">
                <div class="form-group">
                    <label for="description">Description</label>
                    <input id="description" name="description" class="form-control" maxlength="20"
                           v-model="provisionInformation.label"
                           v-validate="'required|max:20'"
                           data-vv-as="Description">
                    <label class="error" v-show="vErrors.has('description')">{{ vErrors.first('description') }}</label>
                </div>
                <div class="form-group">
                    <label for="advertiserPhoneNumber">Advertiser Phone Number</label>
                    <phone-number id="advertiserPhoneNumber" name="advertiserPhoneNumber" class="form-control"
                                  v-model="provisionInformation.advertiserPhoneNumber"
                                  v-validate="'required'"
                                  data-vv-as="Advertiser Phone Number">
                    </phone-number>
                    <label class="error" v-show="vErrors.has('advertiserPhoneNumber')">{{ vErrors.first('advertiserPhoneNumber') }}</label>
                </div>
                <div class="form-group">
                    <label for="shouldShowCallerId">Show Caller Id</label>
                    <input type="checkbox" id="shouldShowCallerId" name="shouldShowCallerId" class="form-control" value="1"
                           v-model="provisionInformation.shouldShowCallerId">
                </div>
                <div class="form-group">
                    <label for="ivrProfile">IVR Profile</label>
                    <select id="ivrProfile" name="ivrProfile" class="form-control"
                            v-model="provisionInformation.ivrProfile"
                            v-validate="'required'"
                            data-vv-as="Market Code">
                        <option v-for="ivrProfile in lists.ivrProfiles" :value="ivrProfile.id">{{ ivrProfile.label }}</option>
                    </select>
                    <label class="error" v-show="vErrors.has('ivrProfile')">{{ vErrors.first('ivrProfile') }}</label>
                </div>
                <div class="form-group">
                    <label for="vertical">Vertical</label>
                    <select id="vertical" name="vertical" class="form-control"
                            v-model="provisionInformation.vertical"
                            v-validate="'required'"
                            data-vv-as="Vertical">
                        <option v-for="vertical in lists.verticals" :value="vertical.id">{{ vertical.label }}</option>
                    </select>
                    <label class="error" v-show="vErrors.has('vertical')">{{ vErrors.first('vertical') }}</label>
                </div>
                <div class="form-group">
                    <label for="phonetic">Phonetic Advertiser Name (for text to speech)</label>
                    <input id="phonetic" name="phonetic" class="form-control"
                           v-model="provisionInformation.metadata.phonetic"
                           data-vv-as="phonetic">
                </div>
            </modal>
        </div>
    </div>
</template>

<script>
    import has from 'lodash/has'
    import get from 'lodash/get'
    import pick from 'lodash/pick'
    import store from './store.js'
    import Swal from 'sweetalert2'
    import cloneDeep from 'lodash/cloneDeep'
    import PhoneNumber from '../../utils/phone-number'

    export default {
        name: 'ProvisionNumber',

        store,

        props: {
            advertiserId: Number,
            lists: {
                type:    Object,
                require: true
            }
        },

        data: () => ({
            isProvisionNumberOpen:       false,
            provisionInformation:        {
                id:                    null,
                advertiserId:          null,
                label:                 null,
                advertiserPhoneNumber: null,
                ivrProfile:            null,
                vertical:              null,
                shouldShowCallerId:    false,
                metadata:              {
                    phonetic: null
                }
            },
            provisionInformationDefault: {},
            leaseTable:                  {
                columns: [
                    'id',
                    'label',
                    'advertiserPhoneNumber',
                    'leasedPhoneNumber',
                    'shouldShowCallerId',
                    'ivrProfile',
                    'vertical',
                    'actions',
                ],
                options: {
                    sortable:       [
                        'Label',
                        'advertiserPhoneNumber',
                        'leasedPhoneNumber',
                        'ivrProfile',
                        'vertical',
                    ],
                    headings:       {
                        'advertiserPhoneNumber': 'Advertiser Phone Number',
                        'leasedPhoneNumber':     'Leased Phone Number',
                        'shouldShowCallerId':    'Show Caller Id',
                        'ivrProfile':            'IVR Profile',
                    },
                    columnsClasses: {
                        'actions': 'text-center'
                    }
                }
            }
        }),

        computed: {
            leaseEndpoint() {
                return this.advertiserId > 0
                    ? `/api/fuse/leases?advertiserId=${this.advertiserId}`
                    : null
            },
            isEditing() {
                return this.provisionInformation.id > 0
            }
        },

        methods: {
            validateProvisionNumber() {
                this.$validator.validateAll().then((result) => {
                    if (result) {
                        this.saveProvisionNumber()
                    }
                })
            },

            saveProvisionNumber() {
                this.provisionInformation.advertiserId = this.advertiserId
                const axiosInstance = this.isEditing
                    ? axios.put('/api/fuse/leases/' + this.provisionInformation.id, this.provisionInformation)
                    : axios.post('/api/fuse/leases', this.provisionInformation)

                $.loading.show()
                axiosInstance.then(() => {
                    $.loading.hide()
                    const message = this.isEditing ? 'The information has been updated' : 'A new number has been provisioned'
                    this.closeProvisionNumberModal()
                    this.$refs.table.refresh()
                    new Swal(message)
                }).catch(() => {
                    $.loading.hide()
                    new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error')
                })
            },

            editProvisionNumber(id) {
                $.loading.show()
                axios.get(`/api/fuse/leases/${id}`).then(({ data: { data } }) => {
                    $.loading.hide()
                    this.provisionInformation = pick(data, Object.keys(this.provisionInformation))
                    this.openProvisionNumberModal()
                }).catch(() => {
                    $.loading.hide()
                    new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error')
                })
            },

            deleteProvisionNumber(id) {
                if (!confirm('Are you sure?')) {
                    return
                }

                $.loading.show()
                axios.delete('/api/fuse/leases/' + id).then(() => {
                    this.$refs.table.refresh()
                    $.loading.hide()
                    new Swal('The provisioned number has been deleted!')
                }).catch((error) => {
                    $.loading.hide()
                    if (has(error, 'response.data.adSets')) {
                        const html = error.response.data.adSets.reduce((htmlLinks, adSet) => {
                            const link = `/campaigns/${adSet.campaign_id}/adset/${adSet.id}/edit`
                            const html = `${adSet.campaign.name} / ${adSet.name}`
                            htmlLinks += `<br><a href="${link}" target="_blank">${html}</a>`

                            return htmlLinks
                        }, '');

                        return new Swal({
                            title: '',
                            html: error.response.data.message + '<br>' + html,
                            type: 'error'
                        })
                    }

                    new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error')
                })
            },

            openProvisionNumberModal() {
                this.isProvisionNumberOpen = true
            },

            closeProvisionNumberModal() {
                this.provisionInformation = cloneDeep(this.provisionInformationDefault)
                this.$validator.reset()
                this.isProvisionNumberOpen = false
            },

            get(object, path, defaultValue) {
                return get(object, path, defaultValue)
            },

            getIvrProfileById(id) {
                const found = this.lists.ivrProfiles.find(item => item.id === id)
                return found ? found.label : id
            },

            getVerticalById(id) {
                const found = this.lists.verticals.find(item => item.id === id)
                return found ? found.label : id
            }
        },

        mounted() {
            this.provisionInformationDefault = cloneDeep(this.provisionInformation)
        },

        components: {
            PhoneNumber
        }
    }
</script>

<style scoped>

</style>
